import React from 'react';
import { Card, Space, Button, Typography, Tag, Row, Col } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useUserTimestampRender } from '@src/hooks';
import { useTypeRender } from './useTypeRender';
import { useGenderRender } from './useGenderRender';
import { useStatusRender } from './useStatusRender';
import { useConversionStatusRender } from './useConversionStatusRender';
import { FaWhatsapp } from 'react-icons/fa';
import dayjs from 'dayjs';

const { Text } = Typography;

interface PatientCardViewProps {
  dataSource: any[];
  onEditClick?: (record: any) => void;
  onDeleteClick?: (record: any) => void;
  onViewClick?: (record: any) => void;
  onRowClick?: (record: any) => void;
  showEdit?: boolean;
  showDelete?: boolean;
  showView?: boolean;
  customActions?: Array<{
    key: string;
    icon: React.ReactNode;
    label: string;
    onClick: (record: any) => void;
    type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
    danger?: boolean;
    tooltip?: string;
    confirmMessage?: string;
  }>;
  rowKey?: string;
}

export const usePatientCardView = () => {
  const getTypeConfig = useTypeRender();
  const getGenderConfig = useGenderRender();
  const getStatusConfig = useStatusRender();
  const getConversionStatusConfig = useConversionStatusRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });

  // Helper function to calculate age from birth date
  const calculateAge = (birthDate: string) => {
    if (!birthDate) return null;
    const age = dayjs().diff(dayjs(birthDate), 'year');
    return age;
  };

  // Helper function to format age with birth date
  const formatAgeWithBirthDate = (birthDate: string, age: number) => {
    if (!birthDate && !age) return 'N/A';
    if (!birthDate && age) return `${age} years`;
    
    const formattedDate = dayjs(birthDate).format('DD MMM YYYY');
    return `${age} years (${formattedDate})`;
  };

  const PatientCardView: React.FC<PatientCardViewProps> = ({dataSource, onEditClick, onDeleteClick, onViewClick, onRowClick, showEdit = true, showDelete = true, showView = false, customActions = [], rowKey = 'id'}) => {
    // Handle patient info click to redirect to patient details
    const handlePatientInfoClick = (record: any, e: any) => {
      e.stopPropagation();
      const url = `${window.location.origin}/patients/${record.id}`;
      window.open(url, '_blank');
    };
    // Helper function to render actions
    const renderActions = (record: any) => {
      const actions = [];

      if (showView) {
        actions.push(
          <Button
            key="view"
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onViewClick?.(record);
            }}
            style={{ color: '#1890ff' }}
          />
        );
      }

      if (showEdit) {
        actions.push(
          <Button
            key="edit"
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onEditClick?.(record);
            }}
            style={{ color: '#faad14' }}
          />
        );
      }

      if (showDelete) {
        actions.push(
          <Button
            key="delete"
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick?.(record);
            }}
            style={{ color: '#ff4d4f' }}
          />
        );
      }

      // Add custom actions
      customActions.forEach((action) => {
        actions.push(
          <Button
            key={action.key}
            type="text"
            size="small"
            icon={action.icon}
            onClick={(e) => {
              e.stopPropagation();
              action.onClick(record);
            }}
            style={{ 
              color: action.danger ? '#ff4d4f' : action.type === 'primary' ? '#1890ff' : '#666'
            }}
          />
        );
      });

      return actions;
    };

    // Helper function to get patient types for badges (for right side)
    const getPatientTypeBadges = (record: any) => {
      const badges: React.ReactNode[] = [];
      
      // Add type badges (Clinical, Aesthetic, etc.)
      if (record.type) {
        const types = typeof record.type === 'string' && record.type.includes(',') 
          ? record.type.split(',').map((t: string) => t.trim())
          : [record.type];
        
        types.forEach((type: string, index: number) => {
          if (type) {
            badges.push(
              <div key={`type-${index}`}>
                {getTypeConfig(type as any)}
              </div>
            );
          }
        });
      }

      return badges;
    };

    // Helper function to get patient status badges (for bottom section)
    const getPatientStatusBadges = (record: any) => {
      const hasPatientStatus = record.patient_status;
      const hasConversionStatus = record.conversion_status;

      if (!hasPatientStatus && !hasConversionStatus) {
        return null;
      }

      return (
        <div style={{ display: 'flex', gap: '16px', marginBottom: '4px' }}>
          {/* Patient Status */}
          {hasPatientStatus && (
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '10px', color: '#999', marginBottom: '2px' }}>Patient Status</div>
              <div>
                {getStatusConfig(record.patient_status)}
              </div>
            </div>
          )}
          
          {/* Conversion Status */}
          {hasConversionStatus && (
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '10px', color: '#999', marginBottom: '2px' }}>Conversion Status</div>
              <div>
                {getConversionStatusConfig(record.conversion_status)}
              </div>
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="p-0">
        {dataSource && dataSource.length > 0 ? dataSource.map((record, index) => {
          const patientName = `${record.first_name || ''} ${record.last_name || ''}`.trim() || 'N/A';
          const patientPhone = record.mobile_no || '';
          const gender = record.gender || '';
          const birthDate = record.birth_date || record.date_of_birth || '';
          const followUpDate = record.follow_up_date || '';

          return (
            <Card
              key={record[rowKey] || index}
              size="small"
              className={`
                mb-4 mx-4 
                ${index === 0 ? 'mt-4' : 'mt-0'}
                rounded-xl shadow-sm border border-gray-200 
                ${onRowClick ? 'cursor-pointer' : 'cursor-default'}
                transition-all duration-200 ease-in-out bg-white
              `}
              bodyStyle={{ padding: '16px' }}
              onClick={() => onRowClick?.(record)}
            >
              {/* Top Section - ID and Gender Badge */}
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2">
                  <Text className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    #{record[rowKey]}
                  </Text>
                  {gender && getGenderConfig(gender)}
                </div>
              </div>

              {/* Patient Info and Type Badges */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div
                    onClick={(e) => handlePatientInfoClick(record, e)}
                    style={{
                      cursor: 'pointer',
                      padding: '4px',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                      border: '1px solid transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                      e.currentTarget.style.borderColor = '#d9d9d9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = 'transparent';
                    }}
                    title="Click to view patient details"
                  >
                    <div className="text-base font-bold text-black mb-1">
                      {patientName}
                    </div>
                    <div className="flex items-center gap-2">
                      <Text className="text-sm text-gray-600">
                        +{patientPhone || 'No phone'}
                      </Text>
                      {patientPhone && (
                        <FaWhatsapp
                          style={{ fontSize: '16px', color: '#25D366' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (patientPhone) {
                              window.open(
                                `https://web.whatsapp.com/send/?phone=${patientPhone}`,
                                '_blank'
                              );
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Patient Type Badges - Right Side */}
                <div className="ml-4">
                  <div className="flex flex-col gap-1">
                    {getPatientTypeBadges(record)}
                  </div>
                </div>
              </div>

              {/* Patient Status Badges */}
              {getPatientStatusBadges(record) && (
                <div className="mb-3">
                  {getPatientStatusBadges(record)}
                </div>
              )}

              {/* Details Section - Birth Date and Follow Up */}
              <Row gutter={[16, 8]} className="mb-3">
                <Col span={12}>
                    <div>
                      <Text type="secondary" className="text-xs">
                        Age
                      </Text>
                      <div className="text-xs font-medium mt-0.5">
                        {formatAgeWithBirthDate(birthDate, record.age || calculateAge(birthDate) || 0)}
                      </div>
                    </div>
                </Col>
                <Col span={12}>
                  <div>
                    <Text type="secondary" className="text-xs">
                      Follow Up
                    </Text>
                    <div className="text-xs font-medium mt-0.5">
                      {followUpDate ? dayjs(followUpDate).format('MMM DD, YYYY hh:mm A') : 'N/A'}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Bottom Section - User & Timestamp */}
              <div className=" flex justify-between items-center border-t border-gray-200 pt-2">
                
                <div className="mt-1">
                  {userTimestampRender(record)}
                </div>
                <div className="flex items-center gap-1">
                  {renderActions(record)}
                </div>
              </div>
            </Card>
          );
        }) : (
          <div className="py-10 px-5 text-center text-gray-500 text-sm">
            No patients found
          </div>
        )}
      </div>
    );
  };

  return { PatientCardView };
};
